import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { getLearn } from "../lib/api";
import { PageTitle, SectionHeading } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { DataState, EmptyState } from "../components/DataState";
import { SkeletonGrid } from "../components/LoadingSkeleton";
import { LessonCard, LEVEL_STYLES } from "../components/learn/LessonCard";

const TEST_IDS = {
  "how-markets-work": "learn-topic-card-how-markets-work",
  "what-is-a-stock": "learn-topic-card-what-is-a-stock",
  "research-a-company": "learn-topic-card-research-a-company",
  "read-financials": "learn-topic-card-read-financials",
  "understanding-risk": "learn-topic-card-understanding-risk",
  "valuation-basics": "learn-topic-card-valuation-basics",
};

export default function Learn() {
  const [topics, setTopics] = useState(null);
  const [error, setError] = useState(false);
  const [level, setLevel] = useState("All");

  const load = () => { setError(false); setTopics(null); getLearn().then((d) => setTopics(d.topics)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const levels = useMemo(() => (topics ? ["All", ...Array.from(new Set(topics.map((t) => t.level)))] : ["All"]), [topics]);
  const filtered = useMemo(() => (!topics ? [] : level === "All" ? topics : topics.filter((t) => t.level === level)), [topics, level]);
  const featured = topics?.[0];

  if (error) return <DataState title="Learning library is temporarily unavailable" detail="LONGVIEW could not load these lessons right now. Please try again shortly." onRetry={load} />;

  return (
    <div className="space-y-8">
      <PageTitle
        kicker="LONGVIEW Learn"
        title="Learn to see the business behind the stock."
        subtitle="Build the financial knowledge needed to understand companies, markets, and the numbers that shape them."
      />

      {!topics && <SkeletonGrid count={6} blockClassName="h-52" />}

      {topics && topics.length === 0 && (
        <EmptyState icon={BookOpen} title="No lessons available yet" detail="LONGVIEW's learning library is being prepared — check back shortly." />
      )}

      {topics && topics.length > 0 && (
        <>
          {featured && (
            <Reveal>
              <Link
                to={`/learn/${featured.id}`}
                data-testid="learn-start-here-card"
                className="lv-panel lv-top-edge group relative block overflow-hidden p-6 transition-colors duration-200 hover:border-amber-500/30 sm:p-8"
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-500/[0.07] blur-3xl" />
                <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="kicker mb-2 text-amber-400">Start Here</div>
                    <h2 className="text-xl font-bold text-white sm:text-2xl">{featured.title}</h2>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-zinc-400">{featured.summary}</p>
                    <div className="mt-3 flex items-center gap-3 font-mono text-[11px] text-zinc-500">
                      <span className={`rounded border px-1.5 py-0.5 uppercase tracking-wide ${LEVEL_STYLES[featured.level] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/25"}`}>{featured.level}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{featured.minutes} min read</span>
                    </div>
                  </div>
                  <span className="lv-press inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 font-mono text-xs font-medium text-amber-300 transition-colors group-hover:bg-amber-500/20">
                    Begin lesson <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          {levels.length > 2 && (
            <div data-testid="learn-level-filters" className="flex flex-wrap items-center gap-2">
              {levels.map((lv) => (
                <button
                  key={lv}
                  data-testid={`learn-filter-${lv.toLowerCase()}`}
                  onClick={() => setLevel(lv)}
                  className={`lv-press rounded-md border px-3 py-1.5 font-mono text-xs transition-colors ${
                    level === lv ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-white/[0.08] text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                  }`}
                >
                  {lv}
                </button>
              ))}
            </div>
          )}

          <div>
            <SectionHeading kicker="Intelligence Library" title="All lessons" />
            {filtered.length === 0 ? (
              <EmptyState icon={BookOpen} title="No lessons in this category" detail="Try a different level filter." />
            ) : (
              <StaggerGrid className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((t) => <StaggerItem key={t.id}><LessonCard t={t} testId={TEST_IDS[t.id]} /></StaggerItem>)}
              </StaggerGrid>
            )}
          </div>
        </>
      )}
    </div>
  );
}
